"use client";

import { useId, useState } from "react";
import Image from "next/image";
import { illustratedCast, illustrationUrl } from "./illustratedCast";

export default function CharacterPicker({
  value,
  onChange,
  disabled = false,
}: {
  value?: string;
  onChange: (characterId: string | undefined) => void;
  disabled?: boolean;
}) {
  const id = useId();
  const [query, setQuery] = useState("");
  const entries = Object.entries(illustratedCast).sort(([left], [right]) => left.localeCompare(right));
  const selected = entries.find(([, character]) => character.slug === value);
  const search = query.trim().toLocaleLowerCase();
  const matches = entries.filter(([name, character]) =>
    `${name} ${character.age} ${character.occupation ?? ""}`.toLocaleLowerCase().includes(search),
  );

  return (
    <fieldset disabled={disabled} className="min-w-0 space-y-3">
      <legend className="text-sm font-medium">Choose their character</legend>
      <p className="text-xs leading-relaxed text-muted-foreground">
        Optional. Leave it and InsuraMentor picks a cast member whose age, gender and life fit the prospect (a couple when the call needs two people). Pick one to keep a specific name, face and voice.
      </p>
      <div className="flex min-h-8 items-center justify-between gap-3 text-xs">
        <p aria-live="polite">
          {selected ? `Selected: ${selected[0]}` : "No character selected — we'll pick the face that fits your prospect"}
        </p>
        {value && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="rounded px-1 py-2 underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring disabled:opacity-50"
          >
            Clear selection
          </button>
        )}
      </div>
      {selected && (
        <p className="text-xs text-muted-foreground">
          {selected[1].partner ? "Both people join the call. Your scenario shapes their concerns." : "Your scenario shapes what they need and how they respond."}
        </p>
      )}
      <label htmlFor={`${id}-search`} className="sr-only">Search characters by name, age, or occupation</label>
      <input
        id={`${id}-search`}
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search name, age, or occupation"
        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring disabled:opacity-50"
      />
      {matches.length ? (
        <div className="grid max-h-72 grid-cols-[repeat(auto-fill,minmax(min(100%,8.5rem),1fr))] gap-2 overflow-y-auto p-1">
          {matches.map(([name, character]) => (
            <label key={character.slug} className="relative min-w-0 cursor-pointer">
              <input
                type="radio"
                name={`${id}-character`}
                value={character.slug}
                checked={value === character.slug}
                onChange={() => onChange(character.slug)}
                className="peer sr-only"
              />
              <span className="block overflow-hidden rounded-md border border-border bg-background peer-checked:border-foreground peer-checked:ring-1 peer-checked:ring-foreground peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ring peer-disabled:cursor-default peer-disabled:opacity-50">
                <Image src={illustrationUrl(character)} alt="" width={192} height={160} sizes="192px" className="aspect-[6/5] w-full bg-[#f4efe7] object-cover object-top" />
                <span className="block px-2.5 py-2">
                  <span className="block text-xs font-medium">{name}</span>
                  {character.partner && <span className="mt-1 block text-[11px] font-medium">Couple · Two voices</span>}
                  <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">{character.age}{character.occupation ? ` · ${character.occupation}` : ""}</span>
                </span>
              </span>
            </label>
          ))}
        </div>
      ) : (
        <p className="py-4 text-sm text-muted-foreground">No characters match. Try another name, age, or occupation.</p>
      )}
    </fieldset>
  );
}
